import {
  Drawer,
  ListItem,
  IconButton,
  Divider,
  ListItemIcon,
  ListItemText,
  List,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Backdrop,
} from "@mui/material";
import React from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NeoSaveModal from "../../modal/SaveModal";
import NeoLoadModal from "../../modal/LoadModal";
import NeoShareModal from "../../modal/ShareModal";
import { NeoAboutModal } from "../../modal/AboutModal";
import { NeoReportExamplesModal } from "../../modal/ReportExamplesModal";
import { Menu } from "@mui/material";
import StorageIcon from '@mui/icons-material/Dataset';
import {
  applicationGetConnection,
  applicationHasAboutModalOpen,
  applicationIsStandalone,
} from "../../application/ApplicationSelectors";
import { connect, useSelector, useDispatch } from "react-redux";
import {
  setAboutModalOpen,
  setConnected,
  setWelcomeScreenOpen,
} from "../../application/ApplicationActions";
import NeoSettingsModal from "../../settings/SettingsModal";
import { getDashboardSettings } from "../DashboardSelectors";
import { updateDashboardSetting } from "../../settings/SettingsActions";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { updateDatabaseThunk } from "../../application/ApplicationThunks";
import { getAllGenieListsToUserNameWithConnectionProps, loadDatabaseListFromNeo4jThunk } from "../DashboardThunks";
import { Neo4jContext, Neo4jContextState } from "use-neo4j/dist/neo4j.context";
import { createNotificationThunk } from "../../page/PageThunks";

export const NeoDrawer = ({
  open,
  hidden,
  connection,
  dashboardSettings,
  updateDashboardSetting,
  handleDrawerClose,
  onAboutModalOpen,
  resetApplication,
  showLoadTooltip,
  setShowLoadTooltip,
}) => {
  const dispatch = useDispatch();
  const { driver } = React.useContext(Neo4jContext);
  const [databases, setDatabases] = React.useState<string[]>([]);
  const [activeDb, setActiveDb] = React.useState("plantgenie");
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showTooltip, setShowTooltip] = React.useState(false);

  // Mapping for display names
  const dbDisplayNames = {
    plantgenie: 'Populus tremula',
    potri: 'Populus trichocarpa',
    piabi: 'Picea abies',
    eugra: 'Eucalyptus grandis'
  };

  React.useEffect(() => {
    // Set default to plantgenie, but use connection database if it's one of the allowed databases
    const allowedDatabases = ['plantgenie', 'potri', 'piabi', 'eugra'];
    const defaultDb = allowedDatabases.includes(connection?.database) ? connection?.database : 'plantgenie';
    setActiveDb(defaultDb);
    dispatch(
      loadDatabaseListFromNeo4jThunk(driver, (data) => {
        console.log("Databases loaded:", data);
        setDatabases(data);
        const availableIndex = data.findIndex(
          (item) => item === connection?.database
        );
        if (availableIndex === -1) {
        }
      })
    );
  }, [connection?.database, driver, dispatch]);
  // Add this entire useEffect block
  React.useEffect(() => {
    const hasSeenTooltip = localStorage.getItem('hasSeenDatabaseTooltip');
    if (!hasSeenTooltip && !open) {
      const timer = setTimeout(() => {
        setShowTooltip(true);
        setTimeout(() => {
          setShowTooltip(false);
          localStorage.setItem('hasSeenDatabaseTooltip', 'true');
        }, 10000);
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [open]);
  const handleDBChange = (event) => {
    setShowTooltip(false); // Add this line
    setActiveDb(event?.target?.value);
    if (event?.target?.value) {
      dispatch(updateDatabaseThunk(event.target.value));
      dispatch(
        getAllGenieListsToUserNameWithConnectionProps(
          driver,
          event.target.value
        )
      );
    }
  };

  if (hidden) {
    return <></>;
  }

  const content = (
    <>
      <Backdrop
        open={showTooltip || showLoadTooltip}
        sx={{
          color: '#fff',
          zIndex: 1200, // Higher than drawer (1201), lower than tooltip
          backgroundColor: 'rgba(0, 0, 0, 0.7)', // 70% black overlay
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }}
        onClick={() => {
          setShowTooltip(false);
          setShowLoadTooltip && setShowLoadTooltip(false);
        }}
      />
      <Drawer
        variant="permanent"
        sx={{
          ...(open
            ? {
              position: "relative",
              overflowX: "hidden",
              width: "240px",
              transition: "width 300ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
              boxShadow: "2px 1px 10px 0px rgb(0 0 0 / 12%)",
              backgroundColor: "#ffffff"
            }
            : {
              position: "relative",
              overflowX: "hidden",
              boxShadow: "2px 1px 10px 0px rgb(0 0 0 / 12%)",
              transition: "width 300ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
              width: "56px",
              backgroundColor: "#ffffff",
            }),
          "& .MuiDrawer-paper": {
            backgroundColor: "#ffffff",
          },
        }}
        open={open == true}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            overflowX: "hidden",
            justifyContent: "flex-end",
            padding: "0 8px",
            minHeight: "44px",
            backgroundColor: "#ffffff",
          }}
        >
          <ListItem>
            <Button
              component="label"
              onClick={resetApplication}
              style={{ color: "#000", marginLeft: "-8px" }}
              variant="outlined"
              size="small"
              startIcon={<ExitToAppIcon />}
            >
              Menu
            </Button>
          </ListItem>
          <IconButton onClick={handleDrawerClose}>
            <ChevronLeftIcon style={{ color: "#000" }} />        </IconButton>
        </div>
        <Box sx={{ px: open ? 2 : 1, pb: 1, mt: 1 }}>
          {open ? (
            <Tooltip
              open={showTooltip}
              title="Select your database here"
              arrow
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#859f64',
                    color: 'white',
                    fontSize: '16px',
                    padding: '8px 12px',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    '& .MuiTooltip-arrow': {
                      color: '#859f64',
                    },
                  },
                },
              }}
            >
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel
                  id="demo-simple-select-label"
                  size="small"
                  sx={{
                    color: "#000",
                    "&.Mui-focused": {
                      color: "#000",
                    },
                  }}
                >
                  Database
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={activeDb}
                  size="small"
                  label="Database"
                  onFocus={() => setShowTooltip(false)}
                  sx={{
                    color: "#000",
                    ".MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.23)",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#C7D7AA",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(0, 0, 0, 0.4)",
                    },
                    ".MuiSvgIcon-root": {
                      fill: "#000 !important",
                    },
                  }}
                  onChange={handleDBChange}
                >
                  {databases
                    .filter((item) =>
                      ['plantgenie', 'potri', 'piabi', 'eugra'].includes(item.toLowerCase())
                    )
                    .map((item) => (
                      <MenuItem key={item} value={item}>
                        {dbDisplayNames[item] || item}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Tooltip>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Tooltip
                open={showTooltip}
                title="Select your database here"
                arrow
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#859f64',
                      color: 'white',
                      fontSize: '16px',
                      padding: '8px 12px',
                      fontWeight: 500,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      '& .MuiTooltip-arrow': {
                        color: '#859f64',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)}
                  sx={{
                    color: '#000',
                    mt: 3,
                    '&:hover': {
                      backgroundColor: '#E4E1D6',

                    },
                  }}
                  title={dbDisplayNames[activeDb] || activeDb || 'Select Database'}
                >
                  <StorageIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                  sx: {
                    bgcolor: '#ffffff',
                    '& .MuiMenuItem-root': {
                      color: '#000',
                      '&:hover': {
                        bgcolor: '#E4E1D6',
                      },
                      '&.Mui-selected': {
                        bgcolor: '#E4E1D6',
                        '&:hover': {
                          bgcolor: '#C7D7AA',
                        },
                      },
                    },
                  },
                }}
              >
                {databases
                  .filter((item) =>
                    ['plantgenie', 'potri', 'piabi', 'eugra'].includes(item.toLowerCase())
                  )
                  .map((item) => (
                    <MenuItem
                      key={item}
                      value={item}
                      selected={item === activeDb}
                      onClick={() => {
                        handleDBChange({ target: { value: item } });
                        setAnchorEl(null);
                      }}
                    >
                      {dbDisplayNames[item] || item}
                    </MenuItem>
                  ))}
              </Menu>
            </Box>
          )}
        </Box>
        <Divider />
        <div>
          <ListItem style={{ height: "47px", backgroundColor: "#ffffff" }}>
            <ListItemIcon />
            <ListItemText primary="" />
          </ListItem>
        </div>
        <Divider />
        <List>
          <div>
            {connection?.roles?.includes('admin') && (
              <NeoSettingsModal
                dashboardSettings={dashboardSettings}
                updateDashboardSetting={updateDashboardSetting}
              />
            )}
            {connection?.roles?.includes('admin') && <NeoSaveModal />}
            <Tooltip
              open={showLoadTooltip}
              title="Load gene list here"
              arrow
              placement="right"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#859f64',
                    color: 'white',
                    fontSize: '16px',
                    padding: '12px 16px',
                    fontWeight: 500,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    zIndex: 1300,
                    '& .MuiTooltip-arrow': {
                      color: '#859f64',
                    },
                  },
                },
                popper: {
                  sx: {
                    zIndex: 1300,
                  },
                },
              }}
            >
              <div onClick={() => setShowLoadTooltip && setShowLoadTooltip(false)}>
                <NeoLoadModal />
              </div>
            </Tooltip>
            <NeoShareModal />
          </div>
        </List>
        <Divider />
        <List>
          <ListItem
            button
            sx={{
              '&:hover': {
                backgroundColor: '#E4E1D6',
              },
            }}
            onClick={(e) =>
              window.open(
                "https://help.plantgenie.org",
                "_blank"
              )
            }
          >
            <ListItemIcon>
              <LibraryBooksIcon style={{ color: "#000" }} />
            </ListItemIcon>
            <ListItemText primary="Documentation" primaryTypographyProps={{ style: { color: "#000" } }} />
          </ListItem>
          {/* <NeoReportExamplesModal database={connection.database} /> */}
          <ListItem
            button
            onClick={onAboutModalOpen}
            sx={{
              '&:hover': {
                backgroundColor: '#E4E1D6',
              },
            }}
          >
            <ListItemIcon>
              <InfoOutlinedIcon style={{ color: "#000" }} />
            </ListItemIcon>
            <ListItemText primary="About" primaryTypographyProps={{ style: { color: "#000" } }} />
          </ListItem>
        </List>
        <Divider />
      </Drawer>
    </>
  );
  return content;
};

const mapStateToProps = (state) => ({
  dashboardSettings: getDashboardSettings(state),
  hidden: applicationIsStandalone(state),
  aboutModalOpen: applicationHasAboutModalOpen(state),
  connection: applicationGetConnection(state),
});

const mapDispatchToProps = (dispatch) => ({
  onAboutModalOpen: (_) => dispatch(setAboutModalOpen(true)),
  updateDashboardSetting: (setting, value) => {
    dispatch(updateDashboardSetting(setting, value));
  },
  resetApplication: (_) => {
    dispatch(setWelcomeScreenOpen(true));
    dispatch(setConnected(false));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(NeoDrawer);